import { createAsyncThunk } from "@reduxjs/toolkit";
import { ThunkApiConfig } from "@shared/models/redux";
import {
  DOCUMENT_TYPE_LOCATION_DOCUMENT,
  DOCUMENT_TYPE_SORT_COLUMN_NAME,
} from "@store/Entities/constants/documentTypeApi";
import { DocumentType } from "../models";

export interface FetchFundProfileDocumentTypesArgs {
  fundGlobalId: string;
}

export interface FetchFundProfileDocumentTypesPayload {
  fundGlobalId: string;
  items: DocumentType[];
}

/**
 * Loads the filtered document type picklist for a Fund profile
 * (GET /documents/v1/DocumentTypes?documentLocation=document&entitledEntityGlobalId={fundGlobalId}).
 *
 * Per-fund data is stored under entities.fundProfileDocumentTypes.data[fundGlobalId],
 * so revisiting a previously loaded fund does not re-hit the API.
 *
 * Server-side filtering by entitledUserId is handled by RequestService (impersonation header).
 * State is reset by the root reducer on impersonation start/stop so a fresh fetch happens automatically.
 */
export const fetchFundProfileDocumentTypes = createAsyncThunk<
  FetchFundProfileDocumentTypesPayload,
  FetchFundProfileDocumentTypesArgs,
  ThunkApiConfig
>(
  "entities/fetchFundProfileDocumentTypes",
  async ({ fundGlobalId }, { rejectWithValue, extra }) => {
    try {
      const response = await extra<DocumentType[]>("/documents/v1/DocumentTypes", {
        queryParams: {
          sortColumn: DOCUMENT_TYPE_SORT_COLUMN_NAME,
          documentLocation: DOCUMENT_TYPE_LOCATION_DOCUMENT,
          entitledEntityGlobalId: fundGlobalId,
        },
      });

      return { fundGlobalId, items: response.data };
    } catch (error) {
      return rejectWithValue("Failed to load document types");
    }
  },
  {
    condition: ({ fundGlobalId }, { getState }) => {
      if (!fundGlobalId) {
        return false;
      }

      const slice = getState().entities.fundProfileDocumentTypes;

      return !slice.loading && !slice.data?.[fundGlobalId];
    },
  }
);
